import React, { useEffect, useState } from 'react';
import {
    fetchEmployeesList,
    deleteEmployee,
    fetchPortsList,
    fetchDesignationsList,
    getEmployeeTargets,
    createEmployeeTarget,
} from './services/api';
import { Designation, Port } from './types';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Button, Image, Form, Row, Col } from 'react-bootstrap';
import { useAuth } from './AuthContext';

const EmployeeTargets: React.FC = () => {
    const [employees, setEmployees] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [savingId, setSavingId] = useState<number | null>(null);

    const [ports, setPorts] = useState<Port[]>([]);
    const [designations, setDesignations] = useState<Designation[]>([]);

    const [filterPort, setFilterPort] = useState('');
    const [filterDesignation, setFilterDesignation] = useState('');
    const [search, setSearch] = useState('');
    const [filtersApplied, setFiltersApplied] = useState(false);

    // 🔹 Target values per employee
    const [targets, setTargets] = useState<Record<number, number | ''>>({});

    const { token } = useAuth();

    useEffect(() => {
        fetchPortsList()
            .then(res => setPorts(Array.isArray(res.data) ? res.data : []))
            .catch(() => setPorts([]));

        fetchDesignationsList()
            .then(res => setDesignations(Array.isArray(res.data) ? res.data : []))
            .catch(() => setDesignations([]));

        if (token?.designation?.user_type === 'ADMIN' && token.port) {
            setFilterPort(String(token.port));
        }
        if (token?.designation?.user_type === 'ADMIN') {
            setSearch(token.employee_code || '');
        }
    }, [token]);

    const handleApplyFilters = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        setLoading(true);
        setError(null);
        setFiltersApplied(true);

        try {
            const params: Record<string, string> = {};
            if (filterPort) params.port_id = filterPort;
            if (filterDesignation) params.designation_id = filterDesignation;
            if (search) params.search = search;
            const res = await getEmployeeTargets(params);
            const list = Array.isArray(res) ? res : [];
            setEmployees(list);


            // preload targets if backend already has them
            const initialTargets: Record<number, number | ''> = {};
            list.forEach((emp: any) => {
                if ((emp as any).target_value !== undefined) {
                    initialTargets[emp.employee_id] = (emp as any).target_value;
                }
            });
            console.log(initialTargets);
            setTargets(initialTargets);
        } catch {
            setError('Failed to load employees');
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    };

    const handleTargetChange = (id: number, value: string) => {
        setTargets(prev => ({
            ...prev,
            [id]: value === '' ? '' : Number(value),
        }));
    };

    const handleSaveTarget = async (employee: any) => {
        const targetValue = targets[employee.employee_id];

        if (targetValue === '' || targetValue === undefined) {
            alert('Please enter target value');
            return;
        }

        setSavingId(employee.employee_id);
        try {
            // 🔹 Replace with real API
            await createEmployeeTarget({ employee_id: employee.employee_id, target: targetValue });
            alert('Target saved successfully');
        } catch (error) {
            alert('Failed to save target');
        } finally {
            setSavingId(null);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Are you sure you want to deactivate this employee?')) return;
        setDeletingId(id);
        try {
            const emp = employees.find(e => e.id === id);
            await deleteEmployee(id, emp);
            setEmployees(es =>
                es.map(e => (e.id === id ? { ...e, is_active: false } : e))
            );
        } catch {
            alert('Failed to deactivate employee');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="p-4 p-md-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0">Support Engineers Task Assignment</h2>

            </div>

            <Form className="mb-3" onSubmit={handleApplyFilters}>
                <Row className="g-2 align-items-end">
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>Location</Form.Label>
                            <Form.Select
                                value={filterPort}
                                onChange={e => setFilterPort(e.target.value)}
                                disabled={!!(token?.designation?.user_type === 'ADMIN' && token.port)}
                            >
                                <option value="">All Locations</option>
                                {ports.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>Designation</Form.Label>
                            <Form.Select
                                value={filterDesignation}
                                onChange={e => setFilterDesignation(e.target.value)}
                            >
                                <option value="">All Designations</option>
                                {designations.map(d => (
                                    <option key={d.id} value={d.id}>
                                        {d.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col md={3} className="d-grid">
                        <Button type="submit" disabled={loading}>
                            {loading ? (
                                <span className="spinner-border spinner-border-sm" />
                            ) : (
                                'Apply Filters'
                            )}
                        </Button>
                    </Col>
                </Row>
            </Form>

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="table-responsive">
                <table className="table table-bordered align-middle">
                    <thead className="table-light">
                        <tr>
                            <th>Profile</th>
                            <th>Name</th>
                            <th>Code</th>
                            <th>Mobile</th>
                            <th>Gender</th>
                            <th>Target</th>
                            <th style={{ width: 140 }}>Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <tr key={i}>
                                    {Array.from({ length: 10 }).map((__, j) => (
                                        <td key={j}>
                                            <Skeleton height={20} />
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : !filtersApplied ? (
                            <tr>
                                <td colSpan={10} className="text-center text-muted">
                                    No data. Please apply filters.
                                </td>
                            </tr>
                        ) : employees.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="text-center">
                                    No employees found.
                                </td>
                            </tr>
                        ) : (
                            employees.map(e => (
                                <tr key={e.employee_id}>
                                    <td>
                                        {e.profile_image ? (
                                            <Image
                                                src={e.profile_image}
                                                roundedCircle
                                                width={40}
                                                height={40}
                                            />
                                        ) : (
                                            <div
                                                className="bg-light rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ width: 40, height: 40 }}
                                            >
                                                <i className="fas fa-user text-secondary" />
                                            </div>
                                        )}
                                    </td>
                                    <td>{e.employee_name}</td>
                                    <td>{e.employee_code}</td>
                                    <td>{e.mobile_number}</td>
                                    <td>{e.gender}</td>

                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            min={0}
                                            value={targets[e.employee_id] ?? ''}
                                            disabled={e.is_active === false}
                                            onChange={ev =>
                                                handleTargetChange(e.employee_id, ev.target.value)
                                            }
                                        />
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-sm btn-outline-success me-2"
                                            onClick={() => handleSaveTarget(e)}
                                            disabled={
                                                savingId === e.id || e.is_active === false
                                            }
                                        >
                                            {savingId === e.id ? (
                                                <span className="spinner-border spinner-border-sm" />
                                            ) : (
                                                <i className="fas fa-save" />
                                            )}
                                        </button>

                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default EmployeeTargets;
